import { useCallback } from 'react';
import {
  isReadModelReady,
  syncEntities,
  syncSalesSnapshot,
  syncExpiredProductsReadModel
} from '../../../services/cacheReadModel';
import { mergeExpiredProductsForDisplay } from '../../../domain/inventory/expiredView';
import { filterValidCustomers } from '../../../utils/salesCacheSync';
import {
  mergePurchasesById,
  mergeSuppliersById,
  mergeEntitiesById,
  normalizeLotsField
} from '../../../utils/businessStateSync';
import { resolveWorkerSessionsForBusiness } from '../../../utils/workerSessionsResolve';
import invoiceService from '../../../services/invoiceService';

/**
 * KV → React partial refresh helpers (Strangler extraction from DataContext).
 */
export function useBusinessRefresh({ setters, currentBusinessId }) {
  const {
    setProducts,
    setPurchases,
    setSales,
    setLots,
    setOrders,
    setSuppliers,
    setInvoices,
    setExpenses,
    setSupermarketPayments,
    setInvoicePayments,
    setSupermarketInvoices,
    setSupplierDebts,
    setCustomers,
    setCustomerDebts,
    setExpiredProducts,
    setNotifications,
    setDiscrepancyManagement,
    setWorkerSessions,
    setReports
  } = setters;

  const setSalesOnly = useCallback(
    (nextOrUpdater) => {
      setSales((prev) =>
        typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater
      );
    },
    [setSales]
  );

  const setPurchasesOnly = useCallback(
    (nextOrUpdater) => {
      setPurchases((prev) =>
        typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater
      );
    },
    [setPurchases]
  );

  const setLotsOnly = useCallback(
    (nextOrUpdater) => {
      setLots((prev) => {
        const next = typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater;
        return Array.isArray(next) ? next : normalizeLotsField(next);
      });
    },
    [setLots]
  );

  const setCustomersOnly = useCallback(
    (nextOrUpdater) => {
      setCustomers((prev) =>
        typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater
      );
    },
    [setCustomers]
  );

  const setSuppliersOnly = useCallback(
    (nextOrUpdater) => {
      setSuppliers((prev) =>
        typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater
      );
    },
    [setSuppliers]
  );

  const setProductsOnly = useCallback(
    (nextOrUpdater) => {
      setProducts((prev) =>
        typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater
      );
    },
    [setProducts]
  );

  const setOrdersOnly = useCallback(
    (nextOrUpdater) => {
      setOrders((prev) =>
        typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater
      );
    },
    [setOrders]
  );

  const setWorkerSessionsOnly = useCallback(
    (nextOrUpdater) => {
      setWorkerSessions((prev) =>
        typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater
      );
    },
    [setWorkerSessions]
  );

  const refreshSalesFromStorage = useCallback(
    async (businessId) => {
      const bid = businessId || currentBusinessId;
      if (!bid) return [];
      const { refreshSalesReadCache } = await import('../../../utils/salesReadSource');
      const list = await refreshSalesReadCache(bid);
      const normalized = Array.isArray(list) ? list : [];
      setSalesOnly(normalized);
      if (isReadModelReady()) {
        // Sales list refresh must not block on worker dashboards / heavy analytics
        syncSalesSnapshot(normalized, { mode: 'silent' });
      }
      return normalized;
    },
    [currentBusinessId, setSalesOnly]
  );

  const refreshPurchasesFromStorage = useCallback(
    async (businessId) => {
      const bid = businessId || currentBusinessId;
      if (!bid) return [];
      const { refreshPurchasesReadCache } = await import('../../../utils/purchasesReadSource');
      const list = await refreshPurchasesReadCache(bid);
      const normalized = Array.isArray(list) ? list : [];
      setPurchases((prev) => mergePurchasesById(prev, normalized));
      if (isReadModelReady()) {
        syncEntities({ purchases: normalized }, { recalc: true });
      }
      return normalized;
    },
    [currentBusinessId, setPurchases]
  );

  const refreshLotsFromStorage = useCallback(
    async (businessId) => {
      const bid = businessId || currentBusinessId;
      if (!bid) return [];
      const { refreshLotsReadCache } = await import('../../../utils/lotsReadSource');
      const list = await refreshLotsReadCache(bid);
      setLotsOnly(Array.isArray(list) ? list : []);
      if (isReadModelReady()) {
        syncEntities({ lots: list }, { recalc: true });
      }
      return list;
    },
    [currentBusinessId, setLotsOnly]
  );

  const refreshOrdersFromStorage = useCallback(
    async (businessId) => {
      const bid = businessId || currentBusinessId;
      if (!bid) return [];
      const { refreshOrdersReadCache } = await import('../../../utils/ordersReadSource');
      const list = await refreshOrdersReadCache(bid);
      setOrders((prev) => mergeEntitiesById(prev, list, ['id', 'orderId']));
      if (isReadModelReady()) {
        syncEntities({ orders: list }, { recalc: true });
      }
      return list;
    },
    [currentBusinessId, setOrders]
  );

  const refreshSuppliersFromStorage = useCallback(
    async (businessId) => {
      const bid = businessId || currentBusinessId;
      if (!bid) return [];
      const { refreshSuppliersReadCache } = await import('../../../utils/suppliersReadSource');
      const list = await refreshSuppliersReadCache(bid);
      setSuppliers((prev) => mergeSuppliersById(prev, list));
      if (isReadModelReady()) {
        syncEntities({ suppliers: list }, { recalc: true });
      }
      return list;
    },
    [currentBusinessId, setSuppliers]
  );

  const refreshInvoicesFromStorage = useCallback(
    async (businessId) => {
      const bid = businessId || currentBusinessId;
      if (!bid) return [];
      const { refreshInvoicesReadCache } = await import('../../../utils/invoicesReadSource');
      const list = await refreshInvoicesReadCache(bid);
      setInvoices((prev) => mergeEntitiesById(prev, list, ['id', 'invoiceNumber']));
      return list;
    },
    [currentBusinessId, setInvoices]
  );

  const refreshExpensesFromStorage = useCallback(
    async (businessId) => {
      const bid = businessId || currentBusinessId;
      if (!bid) return [];
      const { refreshExpensesReadCache } = await import('../../../utils/expensesReadSource');
      const list = await refreshExpensesReadCache(bid);
      const normalized = Array.isArray(list) ? list : [];
      setExpenses((prev) => mergeEntitiesById(prev, normalized, ['id', 'expenseId']));
      if (isReadModelReady()) {
        syncEntities({ expenses: normalized }, { recalc: true });
      }
      return normalized;
    },
    [currentBusinessId, setExpenses]
  );

  const refreshSupermarketPaymentsFromStorage = useCallback(
    async (businessId) => {
      const bid = businessId || currentBusinessId;
      if (!bid) return [];
      const { refreshSupermarketPaymentsReadCache } = await import(
        '../../../utils/supermarketPaymentsReadSource'
      );
      const list = await refreshSupermarketPaymentsReadCache(bid);
      const normalized = Array.isArray(list) ? list : [];
      setSupermarketPayments((prev) => mergeEntitiesById(prev, normalized, ['id', 'paymentId']));
      if (isReadModelReady()) {
        syncEntities({ supermarketPayments: normalized }, { recalc: true });
      }
      return normalized;
    },
    [currentBusinessId, setSupermarketPayments]
  );

  const refreshInvoicePaymentsFromStorage = useCallback(
    async (businessId) => {
      const bid = businessId || currentBusinessId;
      if (!bid) return [];
      const { refreshInvoicePaymentsReadCache } = await import(
        '../../../utils/invoicePaymentsReadSource'
      );
      const list = await refreshInvoicePaymentsReadCache(bid);
      const normalized = Array.isArray(list) ? list : [];
      setInvoicePayments((prev) => mergeEntitiesById(prev, normalized, ['id', 'paymentId']));
      if (isReadModelReady()) {
        syncEntities({ invoicePayments: normalized }, { recalc: true });
      }
      return normalized;
    },
    [currentBusinessId, setInvoicePayments]
  );

  const refreshSupermarketInvoicesFromStorage = useCallback(
    async (businessId) => {
      const bid = businessId || currentBusinessId;
      if (!bid) return [];
      const { refreshSupermarketInvoicesReadCache } = await import(
        '../../../utils/supermarketInvoicesReadSource'
      );
      const { readSuppliersFromBestSource } = await import('../../../utils/suppliersReadSource');
      const { applySupermarketInvoiceSupplierContactMigration } = await import(
        '../../../utils/supermarketInvoiceSupplierContactMigration'
      );

      const [list, suppliersList] = await Promise.all([
        refreshSupermarketInvoicesReadCache(bid),
        readSuppliersFromBestSource(bid)
      ]);
      const normalized = Array.isArray(list) ? list : [];
      const migration = await applySupermarketInvoiceSupplierContactMigration(
        bid,
        normalized,
        Array.isArray(suppliersList) ? suppliersList : []
      );
      const consolidated = migration.invoices;
      let next = consolidated;
      setSupermarketInvoices((prev) => {
        next = invoiceService.mergeAndConsolidateInvoices(prev, consolidated, [
          'id',
          'invoiceId'
        ]);
        if (!invoiceService.areInvoicesEquivalent(next, consolidated)) {
          void invoiceService.persistSupermarketInvoices(next, bid);
        }
        return next;
      });
      if (isReadModelReady()) {
        syncEntities({ supermarketInvoices: next }, { recalc: true });
      }
      return next;
    },
    [currentBusinessId, setSupermarketInvoices]
  );

  const refreshSupplierDebtsFromStorage = useCallback(
    async (businessId) => {
      const bid = businessId || currentBusinessId;
      if (!bid) return [];
      const { refreshSupplierDebtsReadCache } = await import(
        '../../../utils/supplierDebtsReadSource'
      );
      const list = await refreshSupplierDebtsReadCache(bid);
      const normalized = Array.isArray(list) ? list : [];
      setSupplierDebts((prev) => mergeEntitiesById(prev, normalized, ['id', 'supplierId']));
      if (isReadModelReady()) {
        syncEntities({ supplierDebts: normalized }, { recalc: true });
      }
      return normalized;
    },
    [currentBusinessId, setSupplierDebts]
  );

  const refreshCustomersFromStorage = useCallback(
    async (businessId) => {
      const bid = businessId || currentBusinessId;
      if (!bid) return [];
      const { refreshCustomersReadCache } = await import('../../../utils/customersReadSource');
      const list = filterValidCustomers(await refreshCustomersReadCache(bid));
      setCustomersOnly(list);
      if (isReadModelReady()) {
        syncEntities({ customers: list }, { recalc: true });
      }
      return list;
    },
    [currentBusinessId, setCustomersOnly]
  );

  /** Refresh customerDebts from blob (Créance-Einzug / balance events). No hybrid re-write. */
  const refreshCustomerDebtsFromStorage = useCallback(
    async (businessId) => {
      const bid = businessId || currentBusinessId;
      if (!bid || typeof setCustomerDebts !== 'function') return [];
      try {
        const { readArrayFieldFromMergedBlob } = await import('../../../utils/businessStateSync');
        const list = readArrayFieldFromMergedBlob(bid, 'customerDebts') || [];
        const normalized = Array.isArray(list) ? list : [];
        setCustomerDebts(normalized);
        if (isReadModelReady()) {
          syncEntities({ customerDebts: normalized }, { recalc: true });
        }
        return normalized;
      } catch {
        return [];
      }
    },
    [currentBusinessId, setCustomerDebts]
  );

  const refreshExpiredProductsFromStorage = useCallback(
    async (businessId) => {
      const bid = businessId || currentBusinessId;
      if (!bid) return [];
      const { refreshExpiredProductsReadCache } = await import(
        '../../../utils/expiredProductsReadSource'
      );
      const { readLotsFromBestSource } = await import('../../../utils/lotsReadSource');
      const { readProductsFromBestSource } = await import('../../../utils/productsReadSource');

      const [legacyExpired, lotsList, productsList] = await Promise.all([
        refreshExpiredProductsReadCache(bid),
        readLotsFromBestSource(bid),
        readProductsFromBestSource(bid)
      ]);

      const merged = mergeExpiredProductsForDisplay(
        legacyExpired,
        Array.isArray(lotsList) ? lotsList : [],
        Array.isArray(productsList) ? productsList : []
      );
      setExpiredProducts(merged);

      if (isReadModelReady()) {
        syncExpiredProductsReadModel({
          expiredProducts: merged,
          lots: lotsList,
          products: productsList
        });
      }

      return merged;
    },
    [currentBusinessId, setExpiredProducts]
  );

  const refreshNotificationsFromStorage = useCallback(
    async (businessId) => {
      const bid = businessId || currentBusinessId;
      if (!bid) return [];
      const { refreshNotificationsReadCache } = await import(
        '../../../utils/notificationsReadSource'
      );
      const list = await refreshNotificationsReadCache(bid);
      setNotifications(Array.isArray(list) ? list : []);
      return list;
    },
    [currentBusinessId, setNotifications]
  );

  const refreshDiscrepancyFromStorage = useCallback(
    async (businessId) => {
      const bid = businessId || currentBusinessId;
      if (!bid) return null;
      const { refreshDiscrepancyManagementReadCache } = await import(
        '../../../utils/discrepancyManagementReadSource'
      );
      const doc = await refreshDiscrepancyManagementReadCache(bid);
      const normalized = doc || {
        selfDeviations: [],
        handoverDeviations: [],
        workerTolerances: {},
        pendingReviews: [],
        auditTrail: [],
        notifications: [],
        toleranceSystemEnabled: false
      };
      setDiscrepancyManagement(normalized);
      if (isReadModelReady()) {
        syncEntities({ discrepancyManagement: normalized }, { recalc: true });
      }
      return normalized;
    },
    [currentBusinessId, setDiscrepancyManagement]
  );

  const refreshWorkerSessionsFromStorage = useCallback(
    async (businessId) => {
      const bid = businessId || currentBusinessId;
      if (!bid) return [];
      const { refreshWorkerSessionsReadCache } = await import(
        '../../../utils/workerSessionsReadSource'
      );
      const fromEntity = await refreshWorkerSessionsReadCache(bid);
      const sessions = resolveWorkerSessionsForBusiness(bid, fromEntity);
      setWorkerSessions(Array.isArray(sessions) ? sessions : []);
      return sessions;
    },
    [currentBusinessId, setWorkerSessions]
  );

  const refreshReportsFromStorage = useCallback(
    async (businessId) => {
      const bid = businessId || currentBusinessId;
      if (!bid) return {};
      const { refreshReportsReadCache } = await import('../../../utils/reportsReadSource');
      const doc = await refreshReportsReadCache(bid);
      const normalized = doc && typeof doc === 'object' ? doc : {};
      setReports(normalized);
      return normalized;
    },
    [currentBusinessId, setReports]
  );

  const refreshProductsFromStorage = useCallback(
    async (businessId) => {
      const bid = businessId || currentBusinessId;
      if (!bid) return [];
      const { refreshProductsReadCache } = await import('../../../utils/productsReadSource');
      const list = await refreshProductsReadCache(bid);
      setProductsOnly(Array.isArray(list) ? list : []);
      if (isReadModelReady()) {
        syncEntities({ products: list }, { recalc: true });
      }
      return list;
    },
    [currentBusinessId, setProductsOnly]
  );

  return {
    setSalesOnly,
    setPurchasesOnly,
    setLotsOnly,
    setCustomersOnly,
    setSuppliersOnly,
    setProductsOnly,
    setOrdersOnly,
    setWorkerSessionsOnly,
    refreshSalesFromStorage,
    refreshPurchasesFromStorage,
    refreshLotsFromStorage,
    refreshOrdersFromStorage,
    refreshSuppliersFromStorage,
    refreshInvoicesFromStorage,
    refreshExpensesFromStorage,
    refreshSupermarketPaymentsFromStorage,
    refreshInvoicePaymentsFromStorage,
    refreshSupermarketInvoicesFromStorage,
    refreshSupplierDebtsFromStorage,
    refreshCustomersFromStorage,
    refreshCustomerDebtsFromStorage,
    refreshExpiredProductsFromStorage,
    refreshNotificationsFromStorage,
    refreshDiscrepancyFromStorage,
    refreshWorkerSessionsFromStorage,
    refreshReportsFromStorage,
    refreshProductsFromStorage
  };
}
